/**
 * This file contains every Zod schema for the project.
 */
import { CURRENCIES, ISSUERS, NETWORKS } from "@/data/schemas";
import { DEFAULT_VALUATIONS } from "@/data/valuations";
import { z } from "zod";

export const UserCreditCardSchema = z.object({
  cardId: z.string(),
  name: z.string(),
  issuer: z.string(),
  applicationDate: z.date().refine((d) => d.getTime() < Date.now(), {
    message: "Please choose a date before today.",
  }),
});

export type UserCreditCardPayload = z.infer<typeof UserCreditCardSchema>;
export type UserCreditCard = UserCreditCardPayload & { id: string };

export const SortMethods = {
  ByValue: "By Value",
  ByPercentReturn: "By Percent Return",
};

export const FiltersSchema = z.object({
  sortMethod: z.nativeEnum(SortMethods).default(SortMethods.ByValue),
  name: z.string(),
  issuers: z.array(z.nativeEnum(ISSUERS)),
  networks: z.array(z.nativeEnum(NETWORKS)),
  valuationMethod: z.string(),
  isMilitary: z.enum(["Yes", "No"]),
  businessOrPersonal: z.array(z.enum(["Business", "Personal"])),
  currentUniversalCashbackCurrency: z
    .nativeEnum(CURRENCIES)
    .default(CURRENCIES.USD),
  currentUniversalCashback: z.number().min(0).max(100).default(1),
  maximumSpend: z.coerce.number().optional(),
  minimumSpend: z.coerce.number().optional(),
  minimumAnnualFee: z.coerce.number().optional(),
  maximumAnnualFee: z.coerce.number().optional(),
  maximumMonthlySpend: z.coerce.number().optional(),
  showCardsThatCountTowards524: z.array(z.enum(["Yes", "No"])),
  minimumValue: z.number().min(0).default(0),
  minimumPercentOfHistoricalMax: z.enum([
    "Best Ever",
    "Great (95%)",
    "Good (80%)",
    "Decent (60%)",
    "All",
  ]),
  customValuations: z.any().transform((valuations) => {
    // merge in defaults for anything not set
    return { ...DEFAULT_VALUATIONS, ...valuations };
  }),
  travelOrCashback: z.array(z.enum(["Travel", "Cashback"])),
});

export const StringifiedFiltersSchema = z.string().refine((string_) => {
  // Pass to backend as a string to avoid small record/object ambiguities
  try {
    FiltersSchema.parse(JSON.parse(string_));
    return true;
  } catch (error) {
    console.warn(error);
    return false;
  }
});

export type Filters = z.infer<typeof FiltersSchema>;
